import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Building2, FileText, Users, Scale, Shield, Briefcase, Phone, MapPin, Mail, Landmark, Award } from 'lucide-react';
import { useLanguage, useLp } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { HeroSlideshow } from '../components/HeroSlideshow';

const HomePage = () => {
  const { t } = useLanguage();
  const lp = useLp();

  const featuredServices = [
    { key: 'immobiliare', icon: Building2 },
    { key: 'successioni', icon: FileText },
    { key: 'aziende', icon: Briefcase },
    { key: 'patrimoniali', icon: Shield },
    { key: 'procure', icon: Scale },
    { key: 'consulenza', icon: Users }
  ];

  return (
    <div data-testid="home-page">
      <SEO page="home" />

      {/* ═══ HERO ═══ */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background image with overlay */}
        <HeroSlideshow />

        {/* Gold line accent */}
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#C9A227]/60 to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            {/* Left */}
            <motion.div
              className="lg:col-span-7"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            >
              <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#C9A227] font-semibold mb-6">
                {t('hero.subtitle')}
              </p>

              <h1 className="mb-4">
                <span className="block font-['Pinyon_Script'] text-5xl sm:text-6xl lg:text-7xl text-[#C9A227] tracking-wide leading-tight">
                  {t('ui.home.heroEyebrow')}
                </span>
                <span className="block font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.15] mt-2">
                  Accordini Carillo
                </span>
              </h1>

              <p className="font-['Manrope'] text-white/55 text-base md:text-lg max-w-lg mb-10 leading-relaxed">
                {t('hero.description')}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to={lp('/contatti')}>
                  <Button
                    data-testid="hero-cta-primary"
                    className="bg-[#C9A227] text-[#121826] hover:bg-[#B38E1B] h-13 px-10 uppercase tracking-[0.2em] text-[11px] font-bold rounded-none transition-all duration-300 shadow-[0_4px_20px_rgba(201,162,39,0.3)]"
                  >
                    {t('hero.cta')}
                  </Button>
                </Link>
                <Link to={lp('/servizi')}>
                  <Button
                    data-testid="hero-cta-secondary"
                    variant="outline"
                    className="border-white/25 text-white hover:bg-white/8 hover:border-white/40 h-13 px-10 uppercase tracking-[0.2em] text-[11px] font-bold rounded-none transition-all duration-300"
                  >
                    {t('hero.ctaSecondary')}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Right — Quote card */}
            <motion.div
              className="lg:col-span-5 hidden lg:block"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="relative bg-white/[0.04] backdrop-blur-md border border-[#C9A227]/20 p-10 xl:p-12">
                <div className="absolute -top-3 -left-3 w-6 h-6 border-t-2 border-l-2 border-[#C9A227]" />
                <div className="absolute -bottom-3 -right-3 w-6 h-6 border-b-2 border-r-2 border-[#C9A227]" />

                <div className="flex justify-center mb-8">
                  <img
                    src="/repubblica.png"
                    alt="Repubblica Italiana"
                    className="w-20 h-20 object-contain opacity-70"
                  />
                </div>
                <blockquote className="text-center">
                  <p className="font-['Playfair_Display'] text-lg xl:text-xl text-white/85 italic leading-relaxed mb-5">
                    {t('hero.quote')}
                  </p>
                  <div className="h-[1px] w-16 mx-auto bg-[#C9A227]/40 mb-4" />
                  <cite className="text-[#C9A227] text-xs not-italic tracking-[0.2em] uppercase font-semibold">
                    {t('hero.author')}
                  </cite>
                </blockquote>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
        >
          <div className="w-5 h-9 border border-white/20 rounded-full flex items-start justify-center pt-2">
            <div className="w-1 h-2.5 bg-[#C9A227]/60 rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* ═══ SERVICES ═══ */}
      <section data-testid="services-preview" className="py-16 md:py-24 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-14"
          >
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#C9A227] font-semibold mb-3">
              {t('ui.home.eyebrowServices')}
            </p>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#283880] tracking-tight mb-3">
              {t('services.title')}
            </h2>
            <p className="text-[#5A6578] text-base max-w-xl leading-relaxed">
              {t('services.subtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[1px] bg-[rgba(30,36,51,0.08)]">
            {featuredServices.map(({ key, icon: Icon }, i) => (
              <Link to={lp(`/servizi/${key}`)} key={key}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="group relative bg-white p-8 hover:bg-[#283880] transition-all duration-500 cursor-pointer h-full"
                >
                  <div className="absolute top-0 left-0 w-0 h-[2px] bg-[#C9A227] group-hover:w-full transition-all duration-500" />
                  <div className="flex items-start gap-5">
                    <div className="flex-shrink-0 w-11 h-11 border border-[#C9A227]/30 group-hover:border-[#C9A227]/60 flex items-center justify-center transition-all duration-500">
                      <Icon className="w-5 h-5 text-[#C9A227] stroke-[1.5]" />
                    </div>
                    <div>
                      <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#283880] group-hover:text-white mb-2 transition-colors duration-500">
                        {t(`services.items.${key}.title`)}
                      </h3>
                      <p className="text-[#5A6578] group-hover:text-white/60 text-sm leading-relaxed transition-colors duration-500">
                        {t(`services.items.${key}.desc`)}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-10"
          >
            <Link to={lp('/servizi')}>
              <Button
                data-testid="view-all-services"
                variant="outline"
                className="border-[#283880] text-[#283880] hover:bg-[#283880] hover:text-white h-12 px-8 uppercase tracking-[0.2em] text-[11px] font-bold rounded-none transition-all duration-300"
              >
                {t('hero.ctaSecondary')}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ═══ ABOUT PREVIEW ═══ */}
      <section data-testid="about-preview" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#C9A227] font-semibold mb-3">
                {t('about.subtitle')}
              </p>
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#283880] tracking-tight mb-6">
                {t('about.title')}
              </h2>
              <p className="text-[#5A6578] leading-relaxed mb-6">
                {t('about.description')}
              </p>
              <p className="text-[#5A6578] leading-relaxed mb-8">
                {t('about.team')}
              </p>
              <Link to={lp('/chi-siamo')}>
                <Button
                  data-testid="learn-more-about"
                  variant="outline"
                  className="border-[#283880] text-[#283880] hover:bg-[#283880] hover:text-white h-12 px-8 uppercase tracking-[0.2em] text-[11px] font-bold rounded-none transition-all duration-300"
                >
                  {t('nav.about')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="grid grid-cols-2 gap-[1px] bg-[rgba(30,36,51,0.08)]">
                {[
                  { ...t('ui.home.features')[0], icon: Shield },
                  { ...t('ui.home.features')[1], icon: Award },
                  { ...t('ui.home.features')[2], icon: Landmark },
                  { ...t('ui.home.features')[3], icon: Scale }
                ].map((item, i) => (
                  <div key={i} className="bg-[#FAF8F5] p-6 md:p-8">
                    <item.icon className="w-5 h-5 text-[#C9A227] mb-4 stroke-[1.5]" />
                    <h4 className="font-['Playfair_Display'] text-lg font-semibold text-[#283880] mb-2">
                      {item.title}
                    </h4>
                    <p className="text-[#5A6578] text-sm leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ LOCATIONS ═══ */}
      <section data-testid="locations-section" className="py-16 md:py-24 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-14"
          >
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#C9A227] font-semibold mb-3">
              {t('ui.home.eyebrowLocations')}
            </p>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#283880] tracking-tight">
              {t('contact.subtitle')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {['valeggio', 'peschiera', 'verona'].map((office, index) => (
              <motion.div
                key={office}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group bg-white border border-[rgba(30,36,51,0.1)] hover:border-[#C9A227]/40 transition-all duration-400 shadow-[0_2px_12px_rgba(40,56,128,0.04)]"
              >
                <div className="h-[2px] bg-transparent group-hover:bg-[#C9A227] transition-all duration-500" />
                <div className="p-8">
                  <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#283880] mb-6">
                    {t(`contact.offices.${office}.name`)}
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-4 h-4 text-[#C9A227] mt-0.5 flex-shrink-0 stroke-[1.5]" />
                      <div>
                        <p className="text-[#5A6578] text-sm">{t(`contact.offices.${office}.address`)}</p>
                        <p className="text-[#5A6578] text-sm">{t(`contact.offices.${office}.city`)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="w-4 h-4 text-[#C9A227] flex-shrink-0 stroke-[1.5]" />
                      <a
                        href={`tel:${t(`contact.offices.${office}.phone`).replace(/\s/g, '')}`}
                        className="text-[#283880] font-semibold text-sm hover:text-[#C9A227] transition-colors"
                      >
                        {t(`contact.offices.${office}.phone`)}
                      </a>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section className="relative py-20 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-[#121826]" />
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#C9A227]/40 to-transparent" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#C9A227] font-semibold mb-4">
              {t('ui.home.eyebrowBook')}
            </p>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4">
              {t('contact.title')}
            </h2>
            <p className="text-white/45 text-base md:text-lg mb-10 max-w-2xl mx-auto leading-relaxed">
              {t('contact.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to={lp('/contatti')}>
                <Button
                  data-testid="contact-us-btn"
                  className="bg-[#C9A227] text-[#121826] hover:bg-[#B38E1B] h-13 px-10 uppercase tracking-[0.2em] text-[11px] font-bold rounded-none transition-all duration-300 shadow-[0_4px_20px_rgba(201,162,39,0.25)]"
                >
                  {t('nav.contact')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <a href="mailto:info@accordinicarillo.it" className="flex items-center gap-2 text-white/50 hover:text-[#C9A227] transition-colors text-sm font-medium">
                <Mail className="w-4 h-4" />
                info@accordinicarillo.it
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
